import { useMediaQuery } from '@common/hooks';
import {
  Button,
  Checkbox,
  type DateRange,
  Table,
  type TableSortDirection,
  Tooltip,
} from '@ntconsult/aluma-react';
import { format, parseISO } from 'date-fns';
import { RotateCcw, Trash2 } from 'lucide-react';
import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONVITE,
  ENTREGA,
  IDIOMAS,
  ORIGEM_INCLUSAO,
  SIM_NAO,
} from '../hooks/useFiltrosParticipantes';
import type { FiltrosPainel, ItemPainel } from '../types/Painel';
import {
  type Coluna,
  type ContextoCelula,
  celula,
  identificacao,
  ORDENAVEL,
  ROTULO,
  SO_EXCLUIDOS,
  TAMANHOS,
} from './colunas';

/** Cliques nestas células não chegam à linha (spec 32 R3): o `Checkbox` do Aluma não entrega o evento. */
const naoAbre = (e: MouseEvent) => e.stopPropagation();

/** Em mobile a tabela mostra só Nome (com a resposta ao lado) e Ações; o resto está no detalhe. */
const SECUNDARIA = 'hidden lg:table-cell';
export const COMPACTA = '(max-width: 1023px)';

const paraIso = (d: Date | null) => (d ? format(d, 'yyyy-MM-dd') : '');
const deIso = (s: string) => (s ? parseISO(s) : null);

export type Selecao = { itens: ReadonlyMap<string, ItemPainel>; todosDoFiltro: boolean };

type Props = {
  itens: ItemPainel[];
  filtros: FiltrosPainel;
  alterar: (parcial: Partial<FiltrosPainel>) => unknown;
  colunas: readonly Coluna[];
  selecao: Selecao;
  onAlternar: (id: string) => void;
  onAlternarPagina: () => void;
  onDetalhes: (id: string) => void;
  onAcao: (p: ItemPainel) => void;
};

export function ParticipantesTabela({
  itens,
  filtros,
  alterar,
  colunas,
  selecao,
  onAlternar,
  onAlternarPagina,
  onDetalhes,
  onAcao,
}: Props) {
  const { t, i18n } = useTranslation(['participantes', 'common', 'convites']);
  const compacta = useMediaQuery(COMPACTA);
  const excluidos = filtros.situacao === 'excluido';
  const visiveis = colunas.filter((c) => excluidos || !SO_EXCLUIDOS.includes(c));
  const ctx: ContextoCelula = { t, idioma: i18n.language, compacta };

  const marcada = (id: string) => selecao.todosDoFiltro || selecao.itens.has(id);
  const qtdMarcadas = itens.filter((p) => marcada(p.id)).length;
  const todasMarcadas = itens.length > 0 && qtdMarcadas === itens.length;

  const rotuloAcao = (p: ItemPainel) =>
    t(
      p.situacao === 'elegivel'
        ? 'participantes:lote.excluirLinha'
        : 'participantes:lote.devolverLinha',
      { nome: identificacao(p) },
    );
  const rotulo = (c: Coluna) => t(`participantes:${ROTULO[c]}` as never);

  const sim = (v: string) => t(`participantes:painel.filtros.${v}` as never);
  const opcoes = (valores: readonly string[], texto: (v: string) => string) =>
    valores.map((v) => ({ value: v, label: texto(v) }));
  /** Origem: a API aceita um valor só; vale a última escolha. */
  const unico = (valor: string, set: (v: string) => unknown) => ({
    value: valor ? [valor] : [],
    onValueChange: (v: string[]) => set(v.at(-1) ?? ''),
  });

  function filtroDe(c: Coluna): ReactNode {
    const label = rotulo(c);
    switch (c) {
      case 'matricula':
        return (
          <Table.TextFilter
            label={label}
            value={filtros.matricula}
            onValueChange={(matricula) => alterar({ matricula })}
          />
        );
      case 'nome':
        return (
          <Table.TextFilter
            label={label}
            value={filtros.nome}
            onValueChange={(nome) => alterar({ nome })}
          />
        );
      case 'email':
        return (
          <>
            <Table.TextFilter
              label={label}
              value={filtros.email}
              onValueChange={(email) => alterar({ email })}
            />
            <Table.OptionsFilter
              label={t('participantes:filtros.temEmail')}
              options={opcoes(SIM_NAO, sim)}
              value={filtros.temEmail}
              onValueChange={(temEmail) =>
                alterar({ temEmail: temEmail as FiltrosPainel['temEmail'] })
              }
            />
          </>
        );
      case 'resposta':
        return (
          <>
            <Table.OptionsFilter
              label={label}
              options={opcoes(SIM_NAO, sim)}
              value={filtros.respondeu}
              onValueChange={(respondeu) =>
                alterar({ respondeu: respondeu as FiltrosPainel['respondeu'] })
              }
            />
            <Table.DateRangeFilter
              label={t('participantes:filtros.respondidaEm')}
              value={{ from: deIso(filtros.respondidaDe), to: deIso(filtros.respondidaAte) }}
              onValueChange={(r: DateRange) =>
                alterar({ respondidaDe: paraIso(r.from), respondidaAte: paraIso(r.to) })
              }
            />
          </>
        );
      case 'brinde':
        return (
          <Table.OptionsFilter
            label={label}
            options={opcoes(SIM_NAO, sim)}
            value={filtros.brinde}
            onValueChange={(brinde) => alterar({ brinde: brinde as FiltrosPainel['brinde'] })}
          />
        );
      case 'camiseta':
        return (
          <Table.OptionsFilter
            label={label}
            options={opcoes(TAMANHOS, (v) => v)}
            value={filtros.camiseta}
            onValueChange={(camiseta) => alterar({ camiseta })}
          />
        );
      case 'idioma':
        return (
          <Table.OptionsFilter
            label={label}
            options={opcoes(IDIOMAS, (v) => t(`participantes:idiomas.${v}` as never))}
            value={filtros.idioma}
            onValueChange={(idioma) => alterar({ idioma: idioma as FiltrosPainel['idioma'] })}
          />
        );
      case 'endereco':
        return (
          <Table.OptionsFilter
            label={label}
            options={opcoes(ENTREGA, (v) => t(`participantes:painel.entrega.${v}` as never))}
            value={filtros.entrega}
            onValueChange={(entrega) => alterar({ entrega: entrega as FiltrosPainel['entrega'] })}
          />
        );
      case 'convite':
        return (
          <Table.OptionsFilter
            label={label}
            options={opcoes(CONVITE, (v) => t(`convites:status.${v}` as never))}
            value={filtros.convite}
            onValueChange={(convite) => alterar({ convite: convite as FiltrosPainel['convite'] })}
          />
        );
      case 'acessos':
        return (
          <Table.OptionsFilter
            label={t('participantes:filtros.acessou')}
            options={opcoes(SIM_NAO, sim)}
            value={filtros.acessou}
            onValueChange={(acessou) => alterar({ acessou: acessou as FiltrosPainel['acessou'] })}
          />
        );
      case 'ultimoAcesso':
        return (
          <Table.DateRangeFilter
            label={label}
            value={{ from: deIso(filtros.acessoDe), to: deIso(filtros.acessoAte) }}
            onValueChange={(r: DateRange) =>
              alterar({ acessoDe: paraIso(r.from), acessoAte: paraIso(r.to) })
            }
          />
        );
      case 'origem':
        return (
          <Table.OptionsFilter
            label={label}
            options={opcoes(ORIGEM_INCLUSAO, (v) => t(`participantes:origem.${v}` as never))}
            {...unico(filtros.incluidoOrigem, (incluidoOrigem) =>
              alterar({ incluidoOrigem: incluidoOrigem as FiltrosPainel['incluidoOrigem'] }),
            )}
          />
        );
      default:
        return undefined;
    }
  }

  const ordenacao = (c: Coluna) => {
    const campo = ORDENAVEL[c];
    if (!campo) return {};
    return {
      sortable: true,
      sortDirection: (filtros.ordenar === campo ? filtros.direcao : 'none') as TableSortDirection,
      onSortChange: (next: TableSortDirection) =>
        alterar(next === 'none' ? { ordenar: '' } : { ordenar: campo, direcao: next }),
    };
  };

  return (
    <Table zebra density="compact">
      <Table.Caption className="sr-only">{t('participantes:aba')}</Table.Caption>
      <Table.Head>
        <Table.Row>
          <Table.Header className="w-px">
            {/* Sem `label` o Checkbox do Aluma só tem o input sr-only: a <label> torna a caixa clicável. */}
            {/* biome-ignore lint/a11y/noLabelWithoutControl: o Checkbox do Aluma renderiza o <input> */}
            <label className="inline-flex cursor-pointer">
              <Checkbox
                aria-label={t('participantes:lote.selecionarPaginaRotulo')}
                checked={todasMarcadas}
                indeterminate={qtdMarcadas > 0 && !todasMarcadas}
                onCheckedChange={onAlternarPagina}
              />
            </label>
          </Table.Header>
          {visiveis.map((c) => (
            <Table.Header
              key={c}
              className={c === 'nome' ? undefined : SECUNDARIA}
              filter={filtroDe(c)}
              {...ordenacao(c)}
            >
              {c === 'acessos' ? (
                <span title={t('participantes:acessos.janela')}>{rotulo(c)}</span>
              ) : (
                rotulo(c)
              )}
            </Table.Header>
          ))}
          <Table.Header>
            <span className="sr-only">{t('participantes:painel.colunas.acoes')}</span>
          </Table.Header>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {itens.map((p) => (
          // Linha inteira abre o detalhe, sem `interactive` (spec 32 R1); quem arrasta para copiar
          // não abre (R3).
          <Table.Row
            key={p.id}
            selected={marcada(p.id)}
            className="cursor-pointer hover:bg-muted"
            onClick={() => {
              if (!window.getSelection()?.toString()) onDetalhes(p.id);
            }}
          >
            <Table.Cell
              onClick={(e) => {
                // O espaço em volta da caixa também marca; na <label>, o próprio Checkbox marca (R3).
                naoAbre(e);
                if (!selecao.todosDoFiltro && !(e.target as Element).closest('label'))
                  onAlternar(p.id);
              }}
            >
              {/* biome-ignore lint/a11y/noLabelWithoutControl: o Checkbox do Aluma renderiza o <input> */}
              <label className="inline-flex cursor-pointer">
                <Checkbox
                  aria-label={t('participantes:lote.selecionarLinha', { nome: identificacao(p) })}
                  checked={marcada(p.id)}
                  disabled={selecao.todosDoFiltro}
                  onCheckedChange={() => onAlternar(p.id)}
                />
              </label>
            </Table.Cell>
            {visiveis.map((c) => (
              <Table.Cell key={c} className={c === 'nome' ? undefined : SECUNDARIA}>
                {celula(c, p, ctx)}
              </Table.Cell>
            ))}
            <Table.Cell onClick={naoAbre}>
              <Tooltip content={rotuloAcao(p)}>
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label={rotuloAcao(p)}
                  onClick={() => onAcao(p)}
                >
                  {p.situacao === 'elegivel' ? <Trash2 size={14} /> : <RotateCcw size={14} />}
                </Button>
              </Tooltip>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}
